/* ============================================================
   <custom-buying-panel>  — build-your-own-pack PDP panel
   Handles: plan switching, scent tabs/topics, per-swatch qty,
   subscribe/one-time + frequency, running total, bulk add-to-cart
   wired into the theme's cart drawer.
   ============================================================ */
(function () {
  if (customElements.get("custom-buying-panel")) return;

  // Format cents using the shop's money_format template (handles its placeholders,
  // strips any HTML so it's safe to drop into textContent).
  const money = (cents, format) => {
    const fmt = format || "${{amount}}";
    const withDelims = (num, precision, thousands, decimal) => {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? "," : thousands;
      decimal = decimal == null ? "." : decimal;
      if (isNaN(num) || num == null) return "0";
      const value = (num / 100).toFixed(precision);
      const parts = value.split(".");
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
      return whole + (parts[1] ? decimal + parts[1] : "");
    };
    const key = (fmt.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || "amount";
    let value;
    switch (key) {
      case "amount_no_decimals": value = withDelims(cents, 0); break;
      case "amount_with_comma_separator": value = withDelims(cents, 2, ".", ","); break;
      case "amount_no_decimals_with_comma_separator": value = withDelims(cents, 0, ".", ","); break;
      case "amount_with_space_separator": value = withDelims(cents, 2, " ", ","); break;
      case "amount_no_decimals_with_space_separator": value = withDelims(cents, 0, " ", ""); break;
      case "amount_with_apostrophe_separator": value = withDelims(cents, 2, "'", "."); break;
      default: value = withDelims(cents, 2); break;
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, value).replace(/<\/?[^>]+>/g, "").trim();
  };

  class CustomBuyingPanel extends HTMLElement {
    connectedCallback() {
      try {
        this.config = JSON.parse(this.querySelector("[data-cbp-config]")?.textContent || "{}");
      } catch (e) {
        this.config = {};
      }
      this.moneyFormat = this.config.moneyFormat || "${{amount}}";

      this.swatches = Array.from(this.querySelectorAll("[data-cbp-swatch]"));
      this.atc = this.querySelector("[data-cbp-atc]");
      this.atcText = this.querySelector("[data-cbp-atc-text]");
      this.atcTotal = this.querySelector("[data-cbp-atc-total]");
      this.banner = this.querySelector("[data-cbp-banner]");
      this.errorEl = this.querySelector("[data-cbp-error]");
      this.purchaseWrap = this.querySelector("[data-cbp-purchase]");
      this.heroIllustration = this.querySelector("[data-cbp-hero-illustration]");
      this.heroProduct = this.querySelector("[data-cbp-hero-product]");
      this.heroIllustrationDefault = this.heroIllustration ? this.heroIllustration.getAttribute("src") || "" : "";
      this.heroProductDefault = this.heroProduct ? this.heroProduct.getAttribute("src") || "" : "";

      this.state = {
        plan: this.querySelector("[data-cbp-plan-input]:checked")?.value || "pack",
        purchase: this.querySelector("[data-cbp-po-input]:checked")?.value || "onetime",
        frequency: this.querySelector("[data-cbp-freq]")?.value || "",
        tab: this.querySelector(".cbp__tab.is-active")?.dataset.cbpTab || "classic",
        topic: "all",
      };

      this.bindPlans();
      this.bindTabs();
      this.bindTopics();
      this.bindSwatches();
      this.bindScentDesc();
      this.bindPurchase();
      this.bindAccordions();
      this.atc?.addEventListener("click", () => this.addToCart());

      this.applyStepVisibility();
      this.applyScentVisibility();

      // adopt the server-rendered default picks: preselect renders is-selected
      // with qty 0 — chosen (highlight + hero) but nothing counted yet
      const findPre = (type) =>
        this.swatches.find(
          (s) => s.dataset.cbpType === type && (this.getQty(s) > 0 || s.classList.contains("is-selected"))
        ) || null;
      this.activeCase = findPre("case");
      this.activeRefill = findPre("refill");

      this.ensureDefaults();
      this.updateTopicCounts();

      // reflect both hero layers on load
      if (this.activeRefill) this.featureRefill(this.activeRefill);
      if (this.activeCase) this.featureCase(this.activeCase);
      this.render();
      this.warmHeroCache();
    }

    /* ---------------- hero image cache ---------------- */
    // Fetch + decode a hero image once and remember the promise; swapping to a
    // warmed URL paints instantly instead of flashing the background colour
    // while the network catches up.
    preloadHero(url) {
      if (!url) return Promise.resolve();
      const cache = (this._heroCache = this._heroCache || new Map());
      if (cache.has(url)) return cache.get(url);
      const p = new Promise((resolve) => {
        const img = new Image();
        img.decoding = "async";
        img.onload = () => {
          if (img.decode) img.decode().catch(() => {}).then(resolve);
          else resolve();
        };
        img.onerror = () => resolve();
        img.src = url;
      });
      cache.set(url, p);
      return p;
    }

    // Warm every swatch's hero image during idle time so even the first
    // case/refill switch is instant.
    warmHeroCache() {
      const warm = () => this.swatches.forEach((s) => this.preloadHero(this.heroUrlFor(s)));
      if ("requestIdleCallback" in window) requestIdleCallback(warm, { timeout: 2000 });
      else setTimeout(warm, 300);
    }

    /* ---------------- plans ---------------- */
    bindPlans() {
      this.querySelectorAll("[data-cbp-plan-input]").forEach((input) => {
        input.addEventListener("change", () => {
          this.state.plan = input.value;
          this.applyStepVisibility();
          this.ensureDefaults();
          this.refreshHeroProduct();
          this.render();
        });
      });
    }

    applyStepVisibility() {
      const plan = this.state.plan;
      const showCases = plan === "pack" || plan === "cases";
      const showRefills = plan === "pack" || plan === "refills";
      const casesStep = this.querySelector('[data-cbp-step="cases"]');
      const refillsStep = this.querySelector('[data-cbp-step="refills"]');
      if (casesStep) casesStep.hidden = !showCases;
      if (refillsStep) refillsStep.hidden = !showRefills;
      this.applyHeroVisibility();
    }

    // Pack shows both hero layers; Refills shows only the refill backdrop
    // (no case in front); Cases shows only the case (no refill backdrop).
    applyHeroVisibility() {
      this.dataset.cbpHero = this.state.plan;
    }

    // Make sure the active plan always has one of each required type CHOSEN
    // (cases for pack/cases, refills for pack/refills). Chosen means highlighted
    // and driving the hero — quantity stays 0 until the shopper adds it or
    // clicks Add to cart (which implies qty 1 per chosen option).
    ensureDefaults() {
      const plan = this.state.plan;
      if (plan === "pack" || plan === "cases") this.ensureOneSelected("case");
      if (plan === "pack" || plan === "refills") this.ensureOneSelected("refill");
    }

    ensureOneSelected(type) {
      const items = this.swatches.filter((s) => s.dataset.cbpType === type);
      if (items.some((s) => this.getQty(s) > 0)) return;
      const active = type === "case" ? this.activeCase : this.activeRefill;
      if (active && !active.classList.contains("is-soldout")) return;
      const pick = items.find(
        (s) => !s.classList.contains("is-soldout") && !(type === "refill" && s.hidden)
      );
      if (!pick) return;
      if (type === "case") this.featureCase(pick);
      else this.featureRefill(pick);
    }

    /* ---------------- scent tabs ---------------- */
    bindTabs() {
      // scope to the tab pills only — refill swatches also carry a
      // data-cbp-tab attribute (their classic/sensitive grouping), and an
      // unscoped selector would hijack their clicks and strip is-active
      // from every other selected swatch.
      this.querySelectorAll(".cbp__tab[data-cbp-tab]").forEach((tab) => {
        tab.addEventListener("click", () => {
          this.state.tab = tab.dataset.cbpTab;
          this.querySelectorAll(".cbp__tab[data-cbp-tab]").forEach((t) =>
            t.classList.toggle("is-active", t === tab)
          );
          this.applyScentVisibility();
          this.updateTopicCounts();
        });
      });
    }

    bindTopics() {
      this.querySelectorAll(".cbp__topic[data-cbp-topic]").forEach((pill) => {
        pill.addEventListener("click", () => {
          this.state.topic = pill.dataset.cbpTopic;
          this.querySelectorAll(".cbp__topic[data-cbp-topic]").forEach((p) =>
            p.classList.toggle("is-active", p === pill)
          );
          this.applyScentVisibility();
        });
      });
    }

    applyScentVisibility() {
      this.swatches
        .filter((s) => s.dataset.cbpType === "refill")
        .forEach((s) => {
          const tabOk = !s.dataset.cbpTab || s.dataset.cbpTab === this.state.tab;
          const topicOk =
            this.state.topic === "all" ||
            (s.dataset.cbpTopic || "").split(",").includes(this.state.topic);
          s.hidden = !(tabOk && topicOk);
        });
      this.refreshScentDesc();
    }

    /* ---------------- scent description ---------------- */
    bindScentDesc() {
      this.scentDescEl = this.querySelector("[data-cbp-scent-desc]");
      if (!this.scentDescEl) return;
      this.swatches
        .filter((s) => s.dataset.cbpType === "refill")
        .forEach((s) => {
          s.addEventListener("mouseenter", () => this.showScentDesc(s));
          s.addEventListener("mouseleave", () => this.refreshScentDesc());
          s.addEventListener("focusin", () => this.showScentDesc(s));
          s.addEventListener("focusout", () => this.refreshScentDesc());
        });
      this.refreshScentDesc();
    }

    refreshScentDesc() {
      if (!this.scentDescEl) return;
      const refills = this.swatches.filter(
        (s) => s.dataset.cbpType === "refill" && !s.hidden
      );
      // keep the description in sync with the chosen backdrop while it's
      // visible (qty may be 0 — chosen still counts), else first selected.
      const featured = this.activeRefill && !this.activeRefill.hidden ? this.activeRefill : null;
      const active = featured || refills.find((s) => this.getQty(s) > 0) || refills[0] || null;
      this.activeScent = active;
      this.showScentDesc(active);
    }

    showScentDesc(s) {
      const el = this.scentDescEl;
      if (!el) return;
      const name = (s && s.dataset.cbpName) || "";
      const desc = (s && s.dataset.cbpDesc) || "";
      if (!desc) {
        el.hidden = true;
        el.textContent = "";
        return;
      }
      el.hidden = false;
      el.textContent = "";
      if (name) {
        const b = document.createElement("strong");
        b.textContent = name + ": ";
        el.appendChild(b);
      }
      el.appendChild(document.createTextNode(desc));
    }

    /* ---------------- swatch qty ---------------- */
    bindSwatches() {
      this.swatches.forEach((s) => {
        const add = s.querySelector("[data-cbp-add]");
        const inc = s.querySelector("[data-cbp-inc]");
        const dec = s.querySelector("[data-cbp-dec]");
        add?.addEventListener("click", (e) => { e.stopPropagation(); this.setQty(s, 1); });
        inc?.addEventListener("click", (e) => { e.stopPropagation(); this.setQty(s, this.getQty(s) + 1); });
        dec?.addEventListener("click", (e) => { e.stopPropagation(); this.setQty(s, this.getQty(s) - 1); });
        // clicking the tile itself only SELECTS it (highlight + hero preview) —
        // it never touches quantity. Quantities are set exclusively through the
        // explicit Add button / +− stepper, and nothing reaches the Shopify
        // cart until the big Add to cart button is clicked.
        s.addEventListener("click", () => {
          if (s.classList.contains("is-soldout")) return;
          if (s.dataset.cbpType === "refill") this.featureRefill(s);
          else if (s.dataset.cbpType === "case") this.featureCase(s);
        });
      });
    }

    // clear a swatch's selection without re-running hero/render side-effects
    clearQty(x) {
      x.dataset.cbpQty = 0;
      x.classList.remove("is-active");
      const n = x.querySelector("[data-cbp-qty-num]");
      if (n) n.textContent = "0";
      const d = x.querySelector("[data-cbp-dec]");
      if (d) d.disabled = true;
    }

    getQty(s) { return parseInt(s.dataset.cbpQty || "0", 10); }

    // Make a refill the chosen one (highlight + hero backdrop) without changing
    // its quantity. The chosen refill counts as qty 1 at Add to cart while the
    // refills step has no explicit quantity.
    featureRefill(s) {
      if (!s || s.dataset.cbpType !== "refill") return;
      this.activeRefill = s;
      this.swatches.forEach((x) => {
        if (x.dataset.cbpType === "refill") x.classList.toggle("is-selected", x === s);
      });
      this.setHeroIllustration(this.heroUrlFor(s));
      this.refreshHeroProduct();
      this.refreshScentDesc();
      this.render();
    }

    // Make a case the chosen one (highlight + foreground product) without
    // changing its quantity.
    featureCase(s) {
      if (!s || s.dataset.cbpType !== "case") return;
      this.activeCase = s;
      this.swatches.forEach((x) => {
        if (x.dataset.cbpType === "case") x.classList.toggle("is-selected", x === s);
      });
      this.refreshHeroProduct();
      this.render();
    }

    setQty(s, qty) {
      qty = Math.max(0, qty);
      s.dataset.cbpQty = qty;
      s.classList.toggle("is-active", qty > 0);
      const numEl = s.querySelector("[data-cbp-qty-num]");
      if (numEl) numEl.textContent = qty;
      const dec = s.querySelector("[data-cbp-dec]");
      if (dec) dec.disabled = qty <= 0;

      // cases are single-select — picking one clears any other selected case
      if (s.dataset.cbpType === "case" && qty > 0) {
        this.swatches.forEach((x) => {
          if (x !== s && x.dataset.cbpType === "case" && this.getQty(x) > 0) this.clearQty(x);
        });
      }

      // gaining quantity also makes the swatch the chosen one (hero + highlight);
      // dropping to 0 keeps it chosen — the pick survives, quantity is simply
      // implied again as 1 at Add to cart. No dead "nothing selected" state.
      if (qty > 0) {
        if (s.dataset.cbpType === "case") this.featureCase(s);
        else if (s.dataset.cbpType === "refill") this.featureRefill(s);
      } else if (s.dataset.cbpType === "refill") {
        this.refreshScentDesc();
      }

      this.render();
    }

    /* ---------------- topic counts ---------------- */
    updateTopicCounts() {
      const refills = this.swatches.filter((s) => s.dataset.cbpType === "refill");
      const inTab = refills.filter(
        (s) => !s.dataset.cbpTab || s.dataset.cbpTab === this.state.tab
      );
      this.querySelectorAll(".cbp__topic[data-cbp-topic]").forEach((btn) => {
        const key = btn.dataset.cbpTopic;
        const countEl = btn.querySelector("[data-cbp-topic-count]");
        let n;
        if (key === "all") {
          n = inTab.length;
        } else {
          n = inTab.filter((s) =>
            (s.dataset.cbpTopic || "").split(",").filter(Boolean).includes(key)
          ).length;
        }
        if (countEl) countEl.textContent = "(" + n + ")";
        const li = btn.closest("li");
        if (li && key !== "all") li.hidden = n === 0;
      });
    }

    // Large hero image for a swatch: prefer its data-cbp-hero, else fall back to the
    // swatch's own <img> (upscaled) so the backdrop works even if the markup didn't
    // emit a dedicated hero URL.
    heroUrlFor(s) {
      if (!s) return "";
      if (s.dataset.cbpHero) return s.dataset.cbpHero;
      const img = s.querySelector(".cbp__swatch-media img");
      const url = img ? img.getAttribute("src") || "" : "";
      return url ? url.replace(/([?&]width=)\d+/, "$11200") : "";
    }

    // The swatch's own product photo (its grid thumbnail, upscaled) — NOT the
    // scenic hero/backdrop image. Used as the front hero layer on the Refills
    // plan, where there is no case to show.
    productImgUrlFor(s) {
      if (!s) return "";
      const img = s.querySelector(".cbp__swatch-media img");
      const url = img ? img.getAttribute("src") || "" : "";
      return url ? url.replace(/([?&]width=)\d+/, "$11200") : "";
    }

    // Front hero layer per plan: Pack & Cases show the chosen case; Refills
    // shows the section's "Refills image" (the plan tile picture), falling back
    // to the chosen refill's own product photo, so the hero never sits empty
    // on a refills-only plan.
    refreshHeroProduct() {
      if (this.state.plan === "refills") {
        this.setHeroProduct(this.dataset.cbpRefillsHero || this.productImgUrlFor(this.activeRefill));
      } else {
        this.setHeroProduct(this.activeCase ? this.heroUrlFor(this.activeCase) : "");
      }
    }

    setHeroProduct(url) {
      const img = this.heroProduct;
      if (!img) return;
      const next = url || this.heroProductDefault;
      this._heroProductNext = next;
      if (!next) {
        img.removeAttribute("src");
        return;
      }
      if (img.getAttribute("src") === next) return;
      // swap only once the new image is decoded — the old one stays on screen,
      // so the background colour never flashes through mid-switch.
      this.preloadHero(next).then(() => {
        if (this._heroProductNext !== next) return;
        img.removeAttribute("srcset");
        img.removeAttribute("sizes");
        img.src = next;
      });
    }

    // The section background is a hero image instead of the flat --cbp-hero-bg
    // colour: Pack and Refills use the featured refill's backdrop, Cases uses
    // the selected case's image. The colour only shows through when the
    // relevant swatch has no image (or nothing is selected yet).
    updateSectionBackdrop() {
      // the chosen swatch drives the backdrop even at qty 0 (default pick)
      const pick = (type, preferred) =>
        preferred && preferred.dataset.cbpType === type
          ? preferred
          : this.swatches.find((x) => x.dataset.cbpType === type && this.getQty(x) > 0);
      const caseSel = pick("case", this.activeCase);
      const refillSel = pick("refill", this.activeRefill);
      let src;
      if (this.state.plan === "cases") src = caseSel;
      else if (this.state.plan === "refills") src = refillSel;
      else src = refillSel || caseSel;
      const url = src ? this.heroUrlFor(src) : "";
      this._bgNext = url;
      if (!url) {
        this.style.removeProperty("--cbp-hero-img");
        this.removeAttribute("data-cbp-bg-image");
        return;
      }
      // keep the previous backdrop painted until the new image is decoded —
      // swapping --cbp-hero-img to an unloaded URL is what flashed the flat
      // background colour before.
      this.preloadHero(url).then(() => {
        if (this._bgNext !== url) return;
        this.style.setProperty("--cbp-hero-img", `url("${url}")`);
        this.setAttribute("data-cbp-bg-image", "");
      });
    }

    setHeroIllustration(url) {
      const img = this.heroIllustration;
      if (!img) return;
      const next = url || this.heroIllustrationDefault;
      this._heroIllusNext = next;
      if (!next) {
        img.removeAttribute("src");
      } else if (img.getAttribute("src") !== next) {
        // same preload-then-swap as the product layer: no colour flash.
        this.preloadHero(next).then(() => {
          if (this._heroIllusNext !== next) return;
          img.removeAttribute("srcset");
          img.removeAttribute("sizes");
          img.src = next;
        });
      }
    }

    /* ---------------- purchase options ---------------- */
    bindPurchase() {
      this.querySelectorAll("[data-cbp-po-input]").forEach((input) => {
        input.addEventListener("change", () => {
          this.state.purchase = input.value;
          this.render();
        });
      });
      const freq = this.querySelector("[data-cbp-freq]");
      freq?.addEventListener("change", () => { this.state.frequency = freq.value; });
    }

    /* ---------------- selections / totals ---------------- */
    // The chosen case/refill counts as qty 1 while its step has no explicit
    // quantity yet — it prices the Add to cart button and drives the hero
    // backdrop. It does NOT enable the button: validity needs explicit
    // quantities (see isValid).
    impliedSwatches() {
      const plan = this.state.plan;
      const need = [];
      if (plan === "pack" || plan === "cases") need.push("case");
      if (plan === "pack" || plan === "refills") need.push("refill");
      const out = [];
      need.forEach((type) => {
        const hasQty = this.swatches.some((x) => x.dataset.cbpType === type && this.getQty(x) > 0);
        if (hasQty) return;
        const active = type === "case" ? this.activeCase : this.activeRefill;
        if (active && !active.classList.contains("is-soldout")) out.push(active);
      });
      return out;
    }

    selections() {
      const plan = this.state.plan;
      const chosen = this.swatches
        .map((s) => ({ el: s, qty: this.getQty(s) }))
        .filter((x) => x.qty > 0);
      this.impliedSwatches().forEach((el) => chosen.push({ el, qty: 1 }));
      return chosen
        .filter((x) => {
          const t = x.el.dataset.cbpType;
          if (plan === "cases") return t === "case";
          if (plan === "refills") return t === "refill";
          return true;
        })
        .map(({ el, qty }) => ({
          type: el.dataset.cbpType,
          id: el.dataset.cbpVariantId,
          name: el.dataset.cbpName || "",
          price: parseInt(el.dataset.cbpPrice || "0", 10),
          sellingPlan: el.dataset.cbpSellingPlan || "",
          qty,
        }));
    }

    // The pack variant whose option values contain BOTH the chosen case name
    // and refill name (e.g. options ["Oudh Spice","Maroon"] for the picks
    // "Maroon" + "Oudh Spice"). Order-agnostic and case-insensitive so it
    // works whichever option position each one holds. Null when the pack
    // product has no variant list or nothing matches.
    packVariantFor(caseName, refillName) {
      const pp = this.config.packProduct;
      const variants = (pp && pp.variants) || [];
      if (!variants.length) return null;
      const norm = (s) => (s || "").trim().toLowerCase();
      const want = [norm(caseName), norm(refillName)].filter(Boolean);
      if (!want.length) return null;
      return (
        variants.find((v) => {
          const opts = (v.options || []).map(norm);
          return want.every((w) => opts.includes(w));
        }) || null
      );
    }

    // Pack-as-product: on the Pack plan the pack product itself (this page's
    // product, from config.packProduct) is the priced cart line — 1 pack
    // consumes 1 case + 1 refill from the selection, and each case+refill
    // pair is resolved to the pack variant matching that combo so the cart
    // carries the real selection, not the default variant. Only what's left
    // over is priced/added individually. Returns null when the pack product
    // isn't configured (or the matched combo is sold out), which falls
    // everything back to the legacy sum-of-parts behaviour.
    packInfo(sel) {
      const pp = this.config.packProduct;
      if (!pp || !pp.id || !pp.available || this.state.plan !== "pack") return null;
      const cases = sel.filter((x) => x.type === "case");
      const refills = sel.filter((x) => x.type === "refill");
      const caseQty = cases.reduce((n, x) => n + x.qty, 0);
      const refillQty = refills.reduce((n, x) => n + x.qty, 0);
      const packQty = Math.min(caseQty, refillQty);
      if (packQty <= 0) return null;
      const consume = (list, need) => {
        const used = [];
        const extra = [];
        list.forEach((x) => {
          const take = Math.min(need, x.qty);
          need -= take;
          if (take > 0) used.push(Object.assign({}, x, { qty: take }));
          if (x.qty - take > 0) extra.push(Object.assign({}, x, { qty: x.qty - take }));
        });
        return { used, extra };
      };
      const c = consume(cases, packQty);
      const r = consume(refills, packQty);

      // pair the consumed case & refill units one-to-one and group identical
      // combos into cart lines at the matching pack variant
      const units = (list) => list.flatMap((x) => Array(x.qty).fill(x));
      const caseUnits = units(c.used);
      const refillUnits = units(r.used);
      const lines = [];
      for (let i = 0; i < packQty; i++) {
        const cu = caseUnits[i] || {};
        const ru = refillUnits[i] || {};
        const v = this.packVariantFor(cu.name, ru.name);
        if (v && !v.available) return null; // this combo is sold out as a pack
        const line = {
          id: v ? v.id : pp.id,
          price: v ? v.price : pp.price,
          matched: !!v,
          caseName: cu.name || "",
          refillName: ru.name || "",
          qty: 1,
        };
        const same = lines.find(
          (l) => l.id === line.id && l.caseName === line.caseName && l.refillName === line.refillName
        );
        if (same) same.qty += 1;
        else lines.push(line);
      }

      return {
        packQty,
        lines,
        cents: lines.reduce((n, l) => n + l.price * l.qty, 0),
        usedCases: c.used,
        usedRefills: r.used,
        extras: c.extra.concat(r.extra),
      };
    }

    // Validity counts EXPLICIT quantities only — the highlighted default picks
    // never enable the button. Pack (starter pack) needs BOTH a case and the
    // minimum refills selected; single-type plans need their own type.
    isValid() {
      const qtyOf = (type) =>
        this.swatches.reduce((n, x) => (x.dataset.cbpType === type ? n + this.getQty(x) : n), 0);
      const cases = qtyOf("case");
      const refills = qtyOf("refill");
      const minRefills = parseInt(this.config.minRefills || 1, 10);
      if (this.state.plan === "cases") return cases > 0;
      if (this.state.plan === "refills") return refills > 0;
      // pack — both must be selected
      return cases > 0 && refills >= minRefills;
    }

    subAvailable() {
      return !!this.config.defaultSellingPlan || Object.keys(this.config.frequencyPlans || {}).length > 0;
    }

    subscribing() {
      return this.subAvailable() && this.state.purchase === "subscription";
    }

    sellingPlanForFrequency() {
      const map = this.config.frequencyPlans || {};
      return map[this.state.frequency] || this.config.defaultSellingPlan || "";
    }

    total(sel) {
      // Pack plan: the pack product's own price covers 1 case + 1 refill;
      // only the leftover extras are priced individually on top.
      const pack = this.packInfo(sel);
      const items = pack ? pack.extras : sel;
      let cents = items.reduce((n, x) => n + x.price * x.qty, 0);
      if (pack) cents += pack.cents;
      if (this.subscribing()) {
        const disc = parseFloat(this.config.subscriptionDiscount || 0);
        if (disc > 0) {
          // discount applies to refill (subscription-eligible) items only —
          // refills inside the pack are covered by the pack price instead
          const refillCents = items
            .filter((x) => x.type === "refill")
            .reduce((n, x) => n + x.price * x.qty, 0);
          cents -= Math.round((refillCents * disc) / 100);
        }
      }
      return cents;
    }

    /* ---------------- render ---------------- */
    render() {
      const sel = this.selections(); // implied default picks included, for pricing
      const valid = this.isValid(); // explicit quantities only
      const totalCents = this.total(sel);

      // purchase options only meaningful once something is selected & a plan supports subscription
      if (this.purchaseWrap) {
        this.purchaseWrap.hidden = !this.subAvailable();
      }

      if (this.atc) {
        this.atc.disabled = !valid;
        if (this.atcText) {
          this.atcText.textContent = valid
            ? (this.subscribing() ? (this.config.ctaSubscribe || "Subscribe") : (this.config.ctaOneTime || "Add to cart"))
            : (this.config.ctaEmpty || "Add products to continue");
        }
        // the pack price stays visible even while the button is disabled
        // waiting for both picks — the shopper always sees what it costs
        if (this.atcTotal) this.atcTotal.textContent = sel.length ? "· " + money(totalCents, this.moneyFormat) : "";
      }

      if (this.banner) {
        // banner reacts to what the shopper explicitly added (within the
        // active plan), not the implied default picks
        const plan = this.state.plan;
        const count = this.swatches.reduce((n, x) => {
          const t = x.dataset.cbpType;
          if (plan === "cases" && t !== "case") return n;
          if (plan === "refills" && t !== "refill") return n;
          return n + this.getQty(x);
        }, 0);
        const msgs = this.config.bannerMessages || {};
        let msg = this.banner.dataset.cbpDefault || "";
        if (count === 1 && msgs.one) msg = msgs.one;
        else if (count === 2 && msgs.two) msg = msgs.two;
        else if (count >= 3 && msgs.three) msg = msgs.three;
        this.banner.textContent = msg;
      }

      this.updateSectionBackdrop();
    }

    /* ---------------- accordions ---------------- */
    bindAccordions() {
      this.querySelectorAll("[data-cbp-ac-head]").forEach((head) => {
        head.addEventListener("click", () => {
          const ac = head.closest("[data-cbp-ac]");
          if (ac) ac.toggleAttribute("open");
        });
      });
    }

    /* ---------------- add to cart ---------------- */
    async addToCart() {
      // only reachable when isValid() passed, i.e. every needed type has
      // explicit quantity — so selections() holds no implied entries here
      const sel = this.selections();
      if (!this.isValid() || !sel.length) return;

      const subscribing = this.subscribing();
      const planForFreq = this.sellingPlanForFrequency();

      const lineFor = (x) => {
        const item = { id: Number(x.id), quantity: x.qty };
        if (subscribing) {
          const plan = x.sellingPlan || (x.type === "refill" ? planForFreq : "");
          if (plan) item.selling_plan = Number(plan);
        }
        return item;
      };

      const pack = this.packInfo(sel);
      let items;
      if (pack) {
        // one line per distinct case+refill combo at the pack variant that
        // matches it — the variant title already names the combo. Line item
        // properties only ride along on the fallback default variant (no
        // options matched) so fulfilment still knows what goes inside.
        const packLines = pack.lines.map((l) => {
          const line = { id: Number(l.id), quantity: l.qty };
          if (!l.matched) {
            line.properties = { Case: l.caseName, Refill: l.refillName };
          }
          return line;
        });
        items = packLines.concat(pack.extras.filter((x) => x.id).map(lineFor));
      } else {
        items = sel.filter((x) => x.id).map(lineFor);
      }

      if (!items.length) {
        this.showError(this.config.errorNoVariants || "These products aren't set up yet.");
        return;
      }

      this.setLoading(true);
      this.showError("");

      const cart = document.querySelector("cart-drawer");
      const sectionIds =
        cart && typeof cart.getSectionsToRender === "function"
          ? cart.getSectionsToRender().map((s) => s.id)
          : [];

      try {
        const res = await fetch(`${window.routes.cart_add_url}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/javascript",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            items,
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        });
        const data = await res.json();

        if (data.status) {
          this.showError(data.description || data.message || "Could not add to cart.");
          if (typeof publish === "function") {
            publish(PUB_SUB_EVENTS.cartError, { source: "custom-buying-panel", message: data.message });
          }
          return;
        }

        if (typeof publish === "function") {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: "custom-buying-panel", cartData: data });
        }

        if (cart && typeof cart.renderContents === "function") {
          // Mirror the origin product template's <product-form>: render the cart
          // sections with isClosedCart=true so cart-drawer.js fires handleFloCartBtn()
          // and the Shopflo checkout opens — exactly how add-to-cart is wired
          // everywhere else in this theme.
          const isClosedCart = !document.body.classList.contains("page-overlay-cart-on");
          cart.renderContents(data, isClosedCart);
        } else {
          window.location = window.routes.cart_url;
        }
      } catch (e) {
        console.error(e);
        this.showError("Something went wrong. Please try again.");
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(on) {
      if (!this.atc) return;
      this.atc.classList.toggle("loading", on);
      this.atc.setAttribute("aria-disabled", on ? "true" : "false");
    }

    showError(msg) {
      if (this.errorEl) this.errorEl.textContent = msg || "";
    }
  }

  customElements.define("custom-buying-panel", CustomBuyingPanel);
})();
